import React, { useState, useEffect, useContext } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  Grid,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import login from "../../queries/login/login";
import { AuthContext } from "../../utils/userProvider";
import Swal from "sweetalert2";

const schema = yup.object().shape({
  email: yup
    .string()
    .required("Required Field")
    .email("Please enter an email address"),
  password: yup.string().required("Required Field"),
});

const Login = (props) => {
  const { register, handleSubmit, errors } = useForm({
    resolver: yupResolver(schema),
  });

  const [displayPasswordReset, setDisplayPasswordReset] = useState(false);
  const [displayPasswordChanged, setDisplayPasswordChanged] = useState(false);

  const { setAuthState } = useContext(AuthContext);
  const history = useHistory();

  useEffect(() => {
    if (localStorage.getItem("userData") != null) {
      let userData = JSON.parse(localStorage.getItem("userData"));
      if (userData.role == "Executive") {
        userData.editAccess = true;
      } else {
        userData.editAccess = false;
      }
      setAuthState(userData);
      if (userData.firstLogin) {
        history.push("/firstLogin");
      } else {
        history.push("/dashboard");
      }
    }
  }, []);

  const onSubmit = (values) => {
    Swal.fire({
      title: "Logging In",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      showCancelButton: false,
      showCloseButton: false,
      willOpen: () => {
        Swal.showLoading();
      },
      didOpen: async () => {
        try {
          const result = await login({ ...values });
          if (result) {
            let userItem = {
              userId: result.user.id,
              name: result.user.name,
              jwt: result.jwt,
              firstLogin: result.user.firstLogin ? true : false,
              role: result.user.accessRole,
              tabAccess: result.user.tabAccess,
            };
            if (userItem.role == "Executive") {
              userItem.editAccess = true;
            } else {
              userItem.editAccess = false;
            }
            setAuthState(userItem);
            localStorage.setItem("userData", JSON.stringify(userItem));
          }
          if (result.user.firstLogin) {
            history.push("/firstLogin");
          } else {
            history.push("/dashboard");
          }
          Swal.close();
        } catch (errorMessage) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: errorMessage,
          });
        }
      },
    });
  };

  useEffect(() => {
    if (props.location.state) {
      if (props.location.state.passwordReset) {
        setDisplayPasswordReset(true);
      }
      if (props.location.state.passwordChanged) {
        setDisplayPasswordChanged(true);
      }
    }
  }, [props.location.state]);

  return (
    <Grid container component="main" sx={{ minHeight: "100vh" }}>
      <Grid
        item
        xs={false}
        sm={4}
        md={5}
        sx={{
          backgroundImage: "url(img/login.jpg)",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(160deg, rgba(0,186,178,0.35) 0%, rgba(13,45,44,0.45) 100%)",
          },
        }}
      />
      <Grid
        item
        xs={12}
        sm={8}
        md={7}
        component={Paper}
        elevation={0}
        square
        sx={{
          display: "flex",
          alignItems: "center",
          bgcolor: "#fbfcfc",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 440,
            mx: "auto",
            px: { xs: 3, sm: 4 },
            py: { xs: 6, sm: 8 },
          }}
        >
          <Box sx={{ mb: 3 }}>
            {displayPasswordReset && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <AlertTitle>Reset Successful</AlertTitle>
                If the email you have entered exists in our system, we will send
                a link to reset your password in a few seconds.
              </Alert>
            )}
            {displayPasswordChanged && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <AlertTitle>Reset Successful</AlertTitle>
                Password changed successfully.
              </Alert>
            )}
          </Box>

          <Box
            component="img"
            src="img/logo.png"
            alt="YSuites"
            sx={{ height: 48, mb: 2.5, display: "block" }}
          />

          <Typography
            component="h1"
            variant="h4"
            sx={{
              color: "primary.main",
              fontWeight: 800,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              mb: 0.5,
            }}
          >
            Middleware
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sign in to manage bookings, promotions, and operations.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              inputRef={register}
              error={errors.email != null}
              helperText={errors.email != null ? errors.email.message : null}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              inputRef={register}
              error={errors.password != null}
              helperText={
                errors.password != null ? errors.password.message : null
              }
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              sx={{ mt: 3, mb: 2, py: 1.25 }}
            >
              Sign In
            </Button>
            <Link href="/forgetpassword" variant="body2" underline="hover">
              Forgot password?
            </Link>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default Login;
